import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { startOfMonth, endOfMonth, format as formatDate } from 'date-fns';
import { storesApi, areaManagersApi, brandsApi } from '../lib/api';
import { Store, ArrowRight, Plus, Search, AlertTriangle, AlertOctagon, Trophy } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import HealthBadge from '../components/HealthBadge';
import DateRangePicker from '../components/DateRangePicker';

/**
 * Mappa la data dell'ultimo import giornaliero in un microbadge:
 *  - oggi/ieri  → puntino verde "agg. oggi/ieri"
 *  - 2-3 gg     → puntino giallo "agg. Xg fa"
 *  - 4+ gg      → puntino rosso "Xg fa" (più carico per attirare attenzione)
 *  - mai        → puntino grigio "mai importato"
 *
 * Il bordo/sfondo dei badge precedenti creava una griglia "rumorosa" quando
 * molti store sono nello stesso stato. Ora il colore è solo nel puntino, il
 * testo resta neutro.
 */
function importStatus(lastDateIso) {
  if (!lastDateIso) {
    return { tone: 'muted',  label: 'Mai importato' };
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const last = new Date(`${lastDateIso}T00:00:00`);
  const days = Math.floor((today - last) / (1000 * 60 * 60 * 24));
  if (days <= 0)  return { tone: 'green',  label: 'agg. oggi' };
  if (days === 1) return { tone: 'green',  label: 'agg. ieri' };
  if (days <= 3)  return { tone: 'yellow', label: `${days}g fa` };
  return            { tone: 'red',    label: `${days}g fa`, urgent: true };
}

const DOT_CLS = {
  green:  'bg-emerald-500',
  yellow: 'bg-amber-500',
  red:    'bg-red-500',
  muted:  'bg-slate-300',
};

export default function StoresPage() {
  const [stores, setStores] = useState([]);
  const [areaManagers, setAreaManagers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedArea, setSelectedArea] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [onlyLate, setOnlyLate] = useState(false);
  const [onlyGaps, setOnlyGaps] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ display_name: '', code: '', brand_id: '', area: '', fresha_reference: '', area_manager_ids: [] });

  // Periodo selezionato (2026-05-12): l'Health Score viene calcolato dal
  // backend sul mese estratto da `dateRange.to`. Default = mese corrente.
  // Per range che coprono più mesi (Q1, YEAR) usiamo il mese del bound
  // superiore: rappresenta il dato di salute più recente del periodo
  // selezionato — coerente con la lettura "stato di salute al ... ".
  const today = new Date();
  const [dateRange, setDateRange] = useState({
    from: formatDate(startOfMonth(today), 'yyyy-MM-dd'),
    to: formatDate(endOfMonth(today), 'yyyy-MM-dd'),
  });

  // Calcola (year, month) da inviare al backend per il filtro Health Score.
  const healthPeriod = useMemo(() => {
    if (!dateRange?.to) return null;
    const d = new Date(dateRange.to);
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  }, [dateRange]);

  const load = useCallback((params = {}) => {
    Promise.all([storesApi.list(params), areaManagersApi.list(), brandsApi.list()])
      .then(([sRes, amRes, bRes]) => { setStores(sRes.data); setAreaManagers(amRes.data); setBrands(bRes.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Refetch stores quando cambia il periodo selezionato.
  useEffect(() => {
    if (!healthPeriod) return;
    storesApi.list(healthPeriod)
      .then(r => setStores(r.data))
      .catch(console.error);
  }, [healthPeriod]);

  // Initial load (stores + masters: brand/AM list non dipendono dal periodo).
  useEffect(() => { load(healthPeriod || {}); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async () => {
    try {
      await storesApi.create({
        ...form,
        area_manager_id: form.area_manager_ids?.[0] || null,
      });
      setDialogOpen(false);
      setForm({ display_name: '', code: '', brand_id: '', area: '', fresha_reference: '', area_manager_ids: [] });
      load(healthPeriod || {});
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    }
  };

  const toggleAM = (amId) => {
    setForm(prev => ({
      ...prev,
      area_manager_ids: prev.area_manager_ids.includes(amId)
        ? prev.area_manager_ids.filter(id => id !== amId)
        : [...prev.area_manager_ids, amId]
    }));
  };

  // When brand filter changes, reset area filter to avoid empty selections
  useEffect(() => { setSelectedArea('all'); }, [selectedBrand]);

  // Filter by brand FIRST, so area tabs and result list both respect the brand selection
  const brandScoped = useMemo(() => (
    selectedBrand === 'all' ? stores : stores.filter(s => s.brand_id === selectedBrand)
  ), [stores, selectedBrand]);

  const areaTabs = useMemo(() => {
    const counts = {};
    brandScoped.forEach(s => {
      const area = s.area || '';
      if (area) counts[area] = (counts[area] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count }));
  }, [brandScoped]);

  const unassignedCount = brandScoped.filter(s => !s.area).length;

  // Sottoinsieme dopo brand+area (NON ancora dopo search e NON dopo onlyLate):
  // è lo scope su cui calcoliamo il counter "X in ritardo" e su cui agisce
  // il filtro "in ritardo".
  const areaScoped = useMemo(() => {
    if (selectedArea === 'unassigned') return brandScoped.filter(s => !s.area);
    if (selectedArea !== 'all')        return brandScoped.filter(s => s.area === selectedArea);
    return brandScoped;
  }, [brandScoped, selectedArea]);

  // Conteggio store "in ritardo" (4+ gg dall'ultimo import o mai importati)
  // calcolato sullo scope brand+area corrente, così cambia coerentemente
  // quando l'utente cambia tab/pill.
  const lateCount = useMemo(() => areaScoped.reduce((acc, s) => {
    const t = importStatus(s.last_daily_import_date);
    return acc + (t.tone === 'red' || t.tone === 'muted' ? 1 : 0);
  }, 0), [areaScoped]);

  // Gap nei dati di setup: store senza budget o senza costi per l'anno corrente.
  // Esposto come microbadge sulle card e come contatore cliccabile per
  // filtrare rapidamente i negozi "buchi neri".
  const gapsCount = useMemo(() => areaScoped.reduce((acc, s) => {
    const hasGap = s.has_costs_current_year === false || s.has_budget_current_year === false;
    return acc + (hasGap ? 1 : 0);
  }, 0), [areaScoped]);

  const filtered = useMemo(() => {
    let result = areaScoped;
    if (onlyLate) {
      result = result.filter(s => {
        const t = importStatus(s.last_daily_import_date);
        return t.tone === 'red' || t.tone === 'muted';
      });
    }
    if (onlyGaps) {
      result = result.filter(s => s.has_costs_current_year === false || s.has_budget_current_year === false);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(s =>
        s.display_name.toLowerCase().includes(q) ||
        (s.brand_name || '').toLowerCase().includes(q) ||
        (s.area_manager_name || '').toLowerCase().includes(q) ||
        (s.code || '').toLowerCase().includes(q)
      );
    }
    return [...result].sort((a, b) =>
      (a.display_name || '').localeCompare(b.display_name || '', 'it', { sensitivity: 'base' })
    );
  }, [areaScoped, onlyLate, onlyGaps, search]);

  /** Negozi auto-creati dall'import Fresha che richiedono ancora il setup
   * di brand/area/AM. Bloccati da `active=false`, ma li mostriamo comunque
   * nel grid con un badge "Da rivedere" così l'OPS sa che esistono e dove
   * cliccare per finire la configurazione. */
  const pendingReviewStores = useMemo(
    () => (areaScoped || []).filter(s => s.pending_review === true),
    [areaScoped],
  );

  /** Top 3 store "migliori" del periodo: ranking per Health Score decrescente,
   * limitato ai negozi con dati sufficienti (`sample >= 1`).
   *
   * NOTA importante (2026-05-11, dopo 2° report utente «top 3 verdi mancanti»):
   * abbiamo rimosso il filtro `band === 'red'` / `band === 'green'`. Il motivo:
   * sul dataset reale può succedere che NESSUN negozio raggiunga la soglia
   * 'green' (≥70 + red-cap 25%) o che NESSUNO scenda a 'red', e i pannelli
   * scomparivano del tutto. Ora mostriamo sempre il top/bottom 3 per score,
   * lasciando che il `HealthBadge` di ogni riga comunichi il vero band del
   * singolo negozio (giallo, verde o rosso). I 2 pannelli quindi sono ora
   * "Top 3 migliori" e "Top 3 da monitorare" — il colore del pannello è
   * solo una metafora visiva, il dato è onesto. */
  const topRedStores = useMemo(() => {
    const candidates = (areaScoped || []).filter(
      s => s.active !== false
        && s.health_score
        && s.health_score.score != null
        && s.health_score.sample >= 1,
    );
    return [...candidates]
      .sort((a, b) => a.health_score.score - b.health_score.score)
      .slice(0, 3);
  }, [areaScoped]);

  const topGreenStores = useMemo(() => {
    const candidates = (areaScoped || []).filter(
      s => s.active !== false
        && s.health_score
        && s.health_score.score != null
        && s.health_score.sample >= 1,
    );
    // Esclude gli store già presenti nel pannello rosso per evitare duplicati
    // quando la rete è piccola (es. <6 negozi con dati).
    const worstIds = new Set(topRedStoresSnapshot(candidates).map(s => s.store_id));
    return [...candidates]
      .filter(s => !worstIds.has(s.store_id))
      .sort((a, b) => b.health_score.score - a.health_score.score)
      .slice(0, 3);
  }, [areaScoped]);

  // Helper isolato (anche fuori dal useMemo) per evitare dipendenza ciclica
  // tra i due memo: ricalcoliamo i "peggiori 3" qui localmente.
  function topRedStoresSnapshot(candidates) {
    return [...candidates]
      .sort((a, b) => a.health_score.score - b.health_score.score)
      .slice(0, 3);
  }

  if (loading) return <div className="p-8"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-4 md:p-8 max-w-[1920px]" data-testid="stores-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Negozi</h1>
          <p className="text-slate-500 mt-1 text-sm md:text-base">
            <span className="whitespace-nowrap">{areaScoped.length} negozi</span>
            {(selectedBrand !== 'all' || selectedArea !== 'all') && <span className="whitespace-nowrap">{' nel filtro'}</span>}
            {lateCount > 0 && (
              <>
                <span className="mx-1 text-slate-300">·</span>
                <button
                  type="button"
                  onClick={() => setOnlyLate(v => !v)}
                  className={`font-semibold underline-offset-4 hover:underline transition-colors whitespace-nowrap ${
                    onlyLate ? 'text-red-700 underline' : 'text-red-600 hover:text-red-700'
                  }`}
                  data-testid="stores-late-count"
                  title={onlyLate
                    ? 'Mostra di nuovo tutti i negozi'
                    : 'Mostra solo i negozi in ritardo (4+ gg dall\'ultimo import)'}
                >
                  {lateCount} in ritardo{onlyLate ? ' · clic per togliere il filtro' : ''}
                </button>
              </>
            )}
            {gapsCount > 0 && (
              <>
                <span className="mx-1 text-slate-300">·</span>
                <button
                  type="button"
                  onClick={() => setOnlyGaps(v => !v)}
                  className={`font-semibold underline-offset-4 hover:underline transition-colors whitespace-nowrap ${
                    onlyGaps ? 'text-amber-700 underline' : 'text-amber-600 hover:text-amber-700'
                  }`}
                  data-testid="stores-gaps-count"
                  title={onlyGaps
                    ? 'Mostra di nuovo tutti i negozi'
                    : 'Mostra solo i negozi con dati di budget o costi mancanti per l\'anno corrente'}
                >
                  {gapsCount} con dati mancanti{onlyGaps ? ' · clic per togliere il filtro' : ''}
                </button>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 md:gap-3 flex-wrap">
          <DateRangePicker
            from={dateRange.from}
            to={dateRange.to}
            onChange={(f, t) => setDateRange({ from: f, to: t })}
            testIdPrefix="stores-daterange"
          />
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-black text-white hover:bg-slate-900 rounded-none" data-testid="add-store-btn">
                <Plus className="w-4 h-4 mr-2" /> <span className="hidden sm:inline">Aggiungi negozio</span><span className="sm:hidden">Aggiungi</span>
              </Button>
            </DialogTrigger>
          <DialogContent className="rounded-none max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">Nuovo negozio</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Nome negozio *</label>
                <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                  placeholder="Es. Milano Centro" className="rounded-none" data-testid="new-store-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Codice</label>
                  <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="Es. MLC" className="rounded-none font-mono" data-testid="new-store-code" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Brand</label>
                  <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                    <SelectTrigger className="rounded-none" data-testid="new-store-brand"><SelectValue placeholder="Seleziona brand" /></SelectTrigger>
                    <SelectContent>
                      {brands.map(b => <SelectItem key={b.brand_id} value={b.brand_id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Area geografica</label>
                <Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })}
                  placeholder="Es. Nord Ovest, Centro, Nord Est" className="rounded-none" data-testid="new-store-area" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Riferimento Fresha</label>
                <Input value={form.fresha_reference} onChange={(e) => setForm({ ...form, fresha_reference: e.target.value })}
                  placeholder="Es. Milano - cc Citylife | Little Italy Barbershop" className="rounded-none text-sm" data-testid="new-store-fresha" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Area Manager</label>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                  {areaManagers.map(am => (
                    <button key={am.area_manager_id} onClick={() => toggleAM(am.area_manager_id)}
                      className={`text-left px-3 py-2 border text-sm transition-colors ${
                        form.area_manager_ids.includes(am.area_manager_id) ? 'border-black bg-slate-900 text-white' : 'border-slate-200 hover:border-slate-400'
                      }`}>
                      {am.name}
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={handleCreate} disabled={!form.display_name}
                className="w-full bg-black text-white hover:bg-slate-900 rounded-none" data-testid="confirm-new-store-btn">
                Crea negozio
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Brand filter — top-level pills */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="brand-filter-row">
        <BrandPill
          label="Tutti i brand"
          count={stores.length}
          active={selectedBrand === 'all'}
          onClick={() => setSelectedBrand('all')}
          testId="brand-pill-all"
        />
        {brands.map(b => {
          const c = stores.filter(s => s.brand_id === b.brand_id).length;
          return (
            <BrandPill
              key={b.brand_id}
              label={b.name}
              count={c}
              empty={c === 0}
              active={selectedBrand === b.brand_id}
              onClick={() => setSelectedBrand(b.brand_id)}
              testId={`brand-pill-${b.brand_id}`}
            />
          );
        })}
      </div>

      {/* Area filter — secondary tabs (subordinate to brand) */}
      <div className="flex gap-0 mb-4 border-b border-slate-200 overflow-x-auto" data-testid="area-filter-tabs">
        <TabBtn label="Tutti i negozi" count={brandScoped.length} active={selectedArea === 'all'} onClick={() => setSelectedArea('all')} testId="filter-tab-all" />
        {areaTabs.map(t => (
          <TabBtn key={t.name} label={t.name} count={t.count} active={selectedArea === t.name} onClick={() => setSelectedArea(t.name)} testId={`filter-tab-${t.name}`} />
        ))}
        {unassignedCount > 0 && (
          <TabBtn label="Non assegnati" count={unassignedCount} active={selectedArea === 'unassigned'} onClick={() => setSelectedArea('unassigned')} testId="filter-tab-unassigned" />
        )}
      </div>

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Cerca negozio..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 rounded-none border-slate-200" data-testid="store-search" />
      </div>

      {pendingReviewStores.length > 0 && (
        <div
          className="mb-6 border border-amber-300 bg-amber-50/50 px-4 md:px-5 py-3 flex items-start gap-3"
          data-testid="pending-review-banner"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-amber-900">
              {pendingReviewStores.length} {pendingReviewStores.length === 1 ? 'negozio' : 'negozi'} da rivedere
            </p>
            <p className="text-xs text-amber-800 mt-0.5">
              Creati automaticamente da un import Fresha. Manca brand, area e Area Manager — finisci il setup per renderli attivi nelle dashboard.
            </p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {pendingReviewStores.slice(0, 8).map(s => (
                <Link
                  key={s.store_id}
                  to={`/stores/${s.store_id}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-white border border-amber-300 text-amber-900 hover:border-amber-500 transition-colors"
                  data-testid={`pending-review-link-${s.store_id}`}
                >
                  {s.display_name}
                </Link>
              ))}
              {pendingReviewStores.length > 8 && (
                <span className="text-[11px] text-amber-700/70 self-center">+{pendingReviewStores.length - 8} altri</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Top 3 verdi + Top 3 rossi sulla stessa riga
          (idea_b5a6848f1466, 2026-05-11). Stack su mobile, side-by-side da
          md+. Ogni pannello compare solo se ha ≥1 negozio nella propria
          fascia: nessun rumore visivo quando un lato è vuoto. */}
      {(topGreenStores.length > 0 || topRedStores.length > 0) && (
        <div
          className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4"
          data-testid="top-stores-row"
        >
          {/* Top verdi */}
          {topGreenStores.length > 0 && (
            <div className="border border-emerald-200 bg-emerald-50/50" data-testid="top-green-stores-panel">
              <div className="px-4 md:px-5 py-3 border-b border-emerald-200/70 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <h2 className="font-heading text-sm font-semibold tracking-tight text-emerald-900">
                  Top {topGreenStores.length === 1 ? 'negozio' : `${topGreenStores.length} negozi`} migliori
                </h2>
                <span className="text-[10px] uppercase tracking-wider text-emerald-700/70 ml-auto hidden sm:inline">
                  health score decrescente
                </span>
              </div>
              <ul className="divide-y divide-emerald-100">
                {topGreenStores.map((store, idx) => (
                  <li key={store.store_id}>
                    <Link
                      to={`/stores/${store.store_id}`}
                      className="flex items-center gap-3 md:gap-4 px-4 md:px-5 py-3 hover:bg-emerald-100/40 transition-colors"
                      data-testid={`top-green-store-${store.store_id}`}
                    >
                      <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 w-5 flex-shrink-0">#{idx + 1}</span>
                      <HealthBadge health={store.health_score} size="md" />
                      <div className="min-w-0 flex-1">
                        <p className="font-heading font-semibold text-sm md:text-base text-slate-900 truncate">{store.display_name}</p>
                        <p className="text-xs text-slate-500 truncate">
                          {store.brand_name}
                          {store.area_manager_name && ` · AM ${store.area_manager_name}`}
                        </p>
                      </div>
                      <div className="hidden sm:flex items-center gap-3 text-[11px] text-emerald-800 font-mono tabular-nums">
                        <span title="KPI verdi nel periodo">
                          {store.health_score.counts.green} verdi
                        </span>
                        <span className="text-emerald-300" aria-hidden="true">·</span>
                        <span title="KPI valutati">{store.health_score.sample} KPI</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Top rossi */}
          {topRedStores.length > 0 && (
            <div className="border border-red-200 bg-red-50/50" data-testid="top-red-stores-banner">
              <div className="px-4 md:px-5 py-3 border-b border-red-200/70 flex items-center gap-2">
                <AlertOctagon className="w-4 h-4 text-red-600 flex-shrink-0" />
                <h2 className="font-heading text-sm font-semibold tracking-tight text-red-900">
                  Top {topRedStores.length === 1 ? 'negozio' : `${topRedStores.length} negozi`} da monitorare
                </h2>
                <span className="text-[10px] uppercase tracking-wider text-red-700/70 ml-auto hidden sm:inline">
                  health score crescente
                </span>
              </div>
              <ul className="divide-y divide-red-100">
                {topRedStores.map((store, idx) => (
                  <li key={store.store_id}>
                    <Link
                      to={`/stores/${store.store_id}`}
                      className="flex items-center gap-3 md:gap-4 px-4 md:px-5 py-3 hover:bg-red-100/40 transition-colors"
                      data-testid={`top-red-store-${store.store_id}`}
                    >
                      <span className="text-[10px] uppercase tracking-wider font-bold text-red-700 w-5 flex-shrink-0">#{idx + 1}</span>
                      <HealthBadge health={store.health_score} size="md" />
                      <div className="min-w-0 flex-1">
                        <p className="font-heading font-semibold text-sm md:text-base text-slate-900 truncate">{store.display_name}</p>
                        <p className="text-xs text-slate-500 truncate">
                          {store.brand_name}
                          {store.area_manager_name && ` · AM ${store.area_manager_name}`}
                        </p>
                      </div>
                      <div className="hidden sm:flex items-center gap-3 text-[11px] text-red-800 font-mono tabular-nums">
                        <span title="KPI rossi nel periodo">
                          {store.health_score.counts.red} rossi
                        </span>
                        <span className="text-red-300" aria-hidden="true">·</span>
                        <span title="KPI valutati">{store.health_score.sample} KPI</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-red-400 flex-shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((store) => {
          const st = importStatus(store.last_daily_import_date);
          return (
          <Link
            key={store.store_id}
            to={`/stores/${store.store_id}`}
            className="bg-white border border-slate-200 p-6 hover:border-slate-400 transition-colors group"
            data-testid={`store-item-${store.store_id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Store className="w-5 h-5 text-slate-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading font-semibold text-lg text-slate-900 truncate">{store.display_name}</h3>
                  <p className="text-sm text-slate-500 truncate">{store.brand_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <HealthBadge health={store.health_score} size="sm" />
                {store.pending_review ? (
                  <span
                    className="inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold border bg-amber-50 text-amber-700 border-amber-300 mt-0.5"
                    title="Negozio creato da import — manca il setup"
                    data-testid={`store-pending-review-${store.store_id}`}
                  >
                    da rivedere
                  </span>
                ) : (
                  <div
                    className={`w-2.5 h-2.5 rounded-full mt-1 ${store.active ? 'bg-emerald-500' : 'bg-slate-300'}`}
                    title={store.active ? 'Negozio attivo' : 'Negozio disattivato'}
                  />
                )}
              </div>
            </div>
            <div className="space-y-1 text-sm text-slate-500">
              <p>Area Manager: <span className="text-slate-700">{store.area_manager_name || '-'}</span></p>
              {store.area && <p>Area: <span className="text-slate-700">{store.area}</span></p>}
            </div>
            {(store.has_costs_current_year === false || store.has_budget_current_year === false) && (
              <div
                className="mt-3 flex flex-wrap gap-1.5"
                data-testid={`store-data-gaps-${store.store_id}`}
              >
                {store.has_budget_current_year === false && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold bg-amber-50 text-amber-700 border border-amber-200"
                    title={`Nessun budget caricato per ${new Date().getFullYear()}. Le metriche vs budget non saranno disponibili.`}
                    data-testid={`store-gap-budget-${store.store_id}`}
                  >
                    <AlertTriangle className="w-3 h-3" /> Budget mancante
                  </span>
                )}
                {store.has_costs_current_year === false && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold bg-rose-50 text-rose-700 border border-rose-200"
                    title={`Nessun costo registrato per ${new Date().getFullYear()}. La marginalità risulta gonfiata (100%).`}
                    data-testid={`store-gap-costs-${store.store_id}`}
                  >
                    <AlertTriangle className="w-3 h-3" /> Costi mancanti
                  </span>
                )}
              </div>
            )}
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2 text-xs">
              <span
                className={`inline-flex items-center gap-1.5 ${st.urgent ? 'text-red-700 font-semibold' : 'text-slate-500'}`}
                title={store.last_daily_import_date
                  ? `Ultimo import giornaliero: ${store.last_daily_import_date}`
                  : 'Nessun import giornaliero registrato'}
                data-testid={`store-import-status-${store.store_id}`}
              >
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${DOT_CLS[st.tone]}`} aria-hidden="true" />
                {st.label}
              </span>
              <span className="text-slate-400 group-hover:text-black inline-flex items-center">
                Apri <ArrowRight className="w-3 h-3 ml-1" />
              </span>
            </div>
          </Link>
          );
        })}
      </div>

      {filtered.length === 0 && (
        (() => {
          // Inviting empty state: a brand was picked but it has no stores yet → teach the user
          if (selectedBrand !== 'all' && brandScoped.length === 0) {
            const brand = brands.find(b => b.brand_id === selectedBrand);
            return (
              <div className="border border-dashed border-slate-300 bg-slate-50/50 py-16 px-8 text-center" data-testid="empty-brand-state">
                <div className="w-14 h-14 mx-auto mb-4 bg-white border border-slate-200 flex items-center justify-center">
                  <Store className="w-6 h-6 text-slate-400" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">
                  Ancora nessun negozio per <span className="text-slate-700">{brand?.name || 'questo brand'}</span>
                </h3>
                <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
                  Quando aggiungi un negozio assegnandolo a questo brand, lo vedrai apparire qui e potrai filtrarlo per area.
                </p>
                <Button
                  onClick={() => {
                    setForm({ display_name: '', code: '', brand_id: selectedBrand, area: '', fresha_reference: '', area_manager_ids: [] });
                    setDialogOpen(true);
                  }}
                  className="mt-5 bg-black text-white hover:bg-slate-900 rounded-none"
                  data-testid="empty-brand-add-first-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Aggiungi il primo negozio {brand?.name ? `per ${brand.name}` : ''}
                </Button>
              </div>
            );
          }
          // Generic empty (search/area filter no match)
          return (
            <div className="text-center py-16 text-slate-400">
              <Store className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nessun negozio trovato</p>
              {(search || selectedArea !== 'all') && (
                <button
                  onClick={() => { setSearch(''); setSelectedArea('all'); }}
                  className="mt-3 text-xs text-slate-600 hover:text-slate-900 underline underline-offset-4"
                  data-testid="reset-filters-btn"
                >
                  Reimposta filtri
                </button>
              )}
            </div>
          );
        })()
      )}
    </div>
  );
}

function TabBtn({ label, count, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
        active ? 'border-black text-black' : 'border-transparent text-slate-400 hover:text-slate-600'
      }`}
      data-testid={testId}
    >
      {label}
      <span className={`ml-1.5 text-xs px-1.5 py-0.5 ${active ? 'bg-black text-white' : 'bg-slate-100 text-slate-500'}`}>
        {count}
      </span>
    </button>
  );
}

function BrandPill({ label, count, active, empty, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`group flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-all whitespace-nowrap ${
        active
          ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
          : empty
            ? 'bg-white text-slate-400 border-dashed border-slate-300 hover:border-slate-500 hover:text-slate-700'
            : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:text-slate-900'
      }`}
    >
      <span>{label}</span>
      <span className={`text-[11px] px-1.5 py-0.5 font-mono ${
        active
          ? 'bg-white/20 text-white'
          : empty
            ? 'bg-slate-50 text-slate-400 group-hover:bg-slate-100'
            : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200'
      }`}>
        {count}
      </span>
    </button>
  );
}
